/*
  D3ChartWrapper wraps a D3Chart.
  Handles creating the chart and calling updateData.
  Handles tooltip logic.

  Example usage:

  <D3ChartWrapper
    D3ChartClass={D3LineGraph}
    className={className}
    data={data}
    options={options}
  />
*/

import cx from 'classnames'
import type { AxisDomain } from 'd3-axis'
import { set, size } from 'lodash/fp'
import PropTypes from 'prop-types'
import type React from 'react'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import ChartTooltip, { type TooltipParams } from '~/components/ChartTooltip'
import D3Chart, {
  type D3ChartAxisOptions,
  type D3ChartClassType,
  type D3ChartLayoutOptions,
} from './D3Chart'
import cs from './d3_chart.scss'

interface D3ChartWrapperProps<
  DataType,
  XDomainType extends AxisDomain,
  YDomainType extends AxisDomain,
  ChartOptions,
> {
  className?: string
  data: DataType
  options?: Partial<ChartOptions>
  layoutOptions?: Partial<D3ChartLayoutOptions>
  axisOptions?: Partial<D3ChartAxisOptions<XDomainType, YDomainType>>
  D3ChartClass: D3ChartClassType<
    D3Chart<DataType, XDomainType, YDomainType>,
    XDomainType,
    YDomainType,
    ChartOptions
  >
  renderTooltipContents?: (tooltipParams: TooltipParams) => React.ReactNode
  chartTooltipTriggerFn?: string
  // Function to call when the chart emits a node click event (wired to chart options as 'onNodeClick').
  onChartClick?: (params: any) => void
  // When provided, the wrapper will pin the tooltip and the chart will programmatically
  // show the tooltip on the node whose timestamp matches this value (in seconds).
  selectedTimestamp?: number
  containerStyle?: React.CSSProperties
  showNoDataMessage?: boolean
  tooltipPosition?: 'right' | 'center'
  reloadKey?: string
}

const D3ChartWrapper = <
  DataType,
  XDomainType extends AxisDomain,
  YDomainType extends AxisDomain,
  ChartOptions,
>({
  data,
  options,
  axisOptions,
  layoutOptions,
  className,
  D3ChartClass,
  renderTooltipContents,
  chartTooltipTriggerFn,
  onChartClick,
  selectedTimestamp,
  containerStyle,
  showNoDataMessage,
  tooltipPosition,
  reloadKey,
}: D3ChartWrapperProps<DataType, XDomainType, YDomainType, ChartOptions>) => {
  const d3Chart = useRef<D3Chart<DataType, XDomainType, YDomainType> | null>(null)
  const container = useRef<HTMLDivElement | null>(null)
  const [hoverTooltipParams, setHoverTooltipParams] = useState<TooltipParams | null>(
    null,
  )
  const [pinnedTooltipParams, setPinnedTooltipParams] = useState<TooltipParams | null>(
    null,
  )
  const [_pinnedTooltip, setPinnedTooltip] = useState<boolean>(false)

  useEffect(() => {
    return () => {
      if (d3Chart.current) {
        d3Chart.current.teardown()
      }
    }
  }, [])

  // Whenever the data changes, update it.
  useLayoutEffect(() => {
    if (data && d3Chart.current) {
      d3Chart.current._updateData(data)
    }
  }, [data])

  const externallyPinned = selectedTimestamp != null

  const onTooltipTrigger = useCallback((__tooltipParams: any) => {
    // Route tooltip events into hover vs pinned channels.
    // - null always clears the hover tooltip (but never clears pinned)
    // - payloads with __source === 'selected' update the pinned tooltip
    // - other payloads update the hover tooltip (shown even while pinned)
    if (__tooltipParams == null) {
      setHoverTooltipParams(null)
      return
    }
    if (__tooltipParams.__source === 'selected') {
      setPinnedTooltipParams(__tooltipParams)
      return
    }
    setHoverTooltipParams(__tooltipParams)
  }, [])

  // Wrap chart click to pin tooltip and forward to consumer
  const handleChartClick = useCallback(
    (params: any) => {
      if (!params) {
        // Background click: clear pinned tooltip and notify consumer to clear selection
        setPinnedTooltip(false)
        setPinnedTooltipParams(null)
        if (onChartClick) onChartClick(null)
        return
      }
      // Node click: pin tooltip and forward payload
      setPinnedTooltip(true)
      setPinnedTooltipParams(params)
      if (onChartClick) onChartClick(params)
    },
    [onChartClick],
  )

  // Whenever the options change, update it.
  useEffect(() => {
    if (data && d3Chart.current) {
      let chartOptions = options || {}
      if (chartTooltipTriggerFn) {
        chartOptions = set(chartTooltipTriggerFn, onTooltipTrigger)(chartOptions)
      }
      // Always wire our click handler so we can pin tooltips on click
      chartOptions = set('onNodeClick', handleChartClick)(chartOptions as any)
      // Inject selected timestamp so chart can programmatically show tooltip
      if (selectedTimestamp != null) {
        chartOptions = set('selectedTimestamp', selectedTimestamp)(chartOptions as any)
      } else {
        chartOptions = set('selectedTimestamp', null)(chartOptions as any)
      }
      d3Chart.current.updateChartOptions(chartOptions)
    }
  }, [
    options,
    handleChartClick,
    selectedTimestamp,
    chartTooltipTriggerFn,
    onTooltipTrigger,
  ])

  // Sync pinned state and tooltip visibility with external selection
  useEffect(() => {
    if (selectedTimestamp != null) {
      setPinnedTooltip(true)
    } else {
      setPinnedTooltip(false)
      setPinnedTooltipParams(null)
    }
  }, [selectedTimestamp])

  // Whenever the container div changes, recreate the line graph.
  // This should happen very rarely.
  // NOTE: There is currently a gotcha regarding placing D3ChartWrappers inside modals that have
  // an expand animation when opened.
  // We use this particular combination of useLayoutEffect and a container ref because
  // the code runs when the container is mounted, before the animation transform is applied.
  // ResizeObservers may be another option, but have more performance concerns.
  useLayoutEffect(() => {
    if (container.current) {
      let chartOptions = options || {}
      if (chartTooltipTriggerFn) {
        chartOptions = set(chartTooltipTriggerFn, onTooltipTrigger)(chartOptions)
      }
      // Always wire our click handler so we can pin tooltips on click
      chartOptions = set('onNodeClick', handleChartClick)(chartOptions as any)
      // Inject selected timestamp on initial construction too
      if (selectedTimestamp != null) {
        chartOptions = set('selectedTimestamp', selectedTimestamp)(chartOptions as any)
      } else {
        chartOptions = set('selectedTimestamp', null)(chartOptions as any)
      }
      d3Chart.current = new D3ChartClass(
        container.current,
        layoutOptions || {},
        axisOptions || {},
        chartOptions || {},
      )
    }
    if (data && d3Chart.current) {
      d3Chart.current._updateData(data)
    }
    return () => {
      if (d3Chart.current) {
        d3Chart.current.teardown()
      }
    }
  }, [reloadKey])

  const onPinnedTooltipClose = () => {
    // If tooltip is externally pinned by selectedTimestamp, ignore close
    if (externallyPinned) return
    setPinnedTooltip(false)
    setPinnedTooltipParams(null)
  }

  const onHoverTooltipClose = () => {
    setHoverTooltipParams(null)
  }

  if (!data || (showNoDataMessage && size(data) === 0)) {
    return <div className={cx(className, cs.chart, cs.noData)}>No data</div>
  }

  return (
    <>
      <div className={cx(className, cs.chart)} style={containerStyle} ref={container} />
      {renderTooltipContents && pinnedTooltipParams && tooltipPosition && (
        <ChartTooltip
          tooltipParams={pinnedTooltipParams}
          renderTooltipContents={renderTooltipContents}
          onTooltipClose={onPinnedTooltipClose}
          position={tooltipPosition}
        />
      )}
      {renderTooltipContents && hoverTooltipParams && tooltipPosition && (
        <ChartTooltip
          tooltipParams={hoverTooltipParams}
          renderTooltipContents={renderTooltipContents}
          onTooltipClose={onHoverTooltipClose}
          position={tooltipPosition}
        />
      )}
    </>
  )
}

D3ChartWrapper.propTypes = {
  className: PropTypes.string,
  // Any dynamic styles for the chart container
  containerStyle: PropTypes.shape({}),
  // Should be a subclass of D3Chart. This is a CLASS, not an INSTANCE.
  D3ChartClass: PropTypes.any,
  data: PropTypes.any,
  options: PropTypes.objectOf(PropTypes.any),
  chartTooltipTriggerFn: PropTypes.string,
  // Click handler to be passed into the chart options (wired to 'onNodeClick')
  onChartClick: PropTypes.func,
  selectedTimestamp: PropTypes.number,
  renderTooltipContents: PropTypes.func,
  tooltipPosition: PropTypes.oneOf(['center', 'right']),
  showNoDataMessage: PropTypes.bool,
  // Modify when you want the d3 chart to completely reinitialize.
  reloadKey: PropTypes.string,
}

D3ChartWrapper.defaultProps = {
  showNoDataMessage: true,
  reloadKey: '',
}

export default D3ChartWrapper
